import React, { useEffect, useState, useMemo } from "react";
import {
    Box,
    Typography,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    MenuItem,
    TablePagination,
    Chip,
    Divider,
    Tooltip
} from "@mui/material";

import {
    Visibility,
    Edit,
    Delete
} from "@mui/icons-material";

import {
    collection,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    Timestamp,
    onSnapshot
} from "firebase/firestore";

import { db } from "../../firebase";
import Swal from "sweetalert2";

function Thoughcustreport() {
    const Toast = Swal.mixin({
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
    });

    const [customers, setCustomers] = useState([]);
    const [throughList, setThroughList] = useState([]);
    const [trips, setTrips] = useState([]);
    const [payments, setPayments] = useState([]);
    const [payModes, setPayModes] = useState([]);

    const [selectedThroughId, setSelectedThroughId] = useState("");
    const [page, setPage] = useState(0);
    const rowsPerPage = 10;

    const [openHistory, setOpenHistory] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerPayments, setCustomerPayments] = useState([]);

    const [openPayment, setOpenPayment] = useState(false);
    const [editPaymentId, setEditPaymentId] = useState(null);

    const [paymentForm, setPaymentForm] = useState({
        amount: "",
        paymentMode: "",
        note: "",
        date: ""
    });

    // ================= FETCH DATA =================
    useEffect(() => {
        const unsubCustomers = onSnapshot(collection(db, "customers"), (snap) => {
            setCustomers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubThrough = onSnapshot(collection(db, "throughdetails"), (snap) => {
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setThroughList(list);
            // Default select the first thought if available and none selected
            if (list.length > 0 && !selectedThroughId) {
                setSelectedThroughId(list[0].id);
            }
        });
        const unsubTrips = onSnapshot(collection(db, "trips"), snap => {
            setTrips(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubPayments = onSnapshot(collection(db, "customer_history"), snap => {
            setPayments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        const unsubPayModes = onSnapshot(collection(db, "payment_modes"), snap => {
            setPayModes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });

        return () => {
            unsubCustomers();
            unsubThrough();
            unsubTrips();
            unsubPayments();
            unsubPayModes();
        };
    }, []);

    // ================= FILTER CUSTOMERS BY SELECTED THOUGHT =================
    const filteredCustomers = useMemo(() => {
        if (!selectedThroughId) return [];
        
        // Filter customers assigned to the selected throughId
        const linked = customers.filter(c => c.throughId === selectedThroughId);

        return linked.map(customer => {
            const totalTrips = trips.filter(t => t.customerId === customer.id).length;
            const totalSale = trips
                .filter(t => t.customerId === customer.id)
                .reduce((sum, trip) => sum + (trip.products?.reduce((s, p) => s + Number(p.amount || 0), 0) || 0), 0);
            
            const totalPaid = payments
                .filter(p => p.customerId === customer.id)
                .reduce((sum, p) => sum + Number(p.amount || 0), 0);
            
            const remaining = totalSale - totalPaid;

            return { ...customer, totalTrips, totalSale, totalPaid, remaining };
        });
    }, [selectedThroughId, customers, trips, payments]);

    const getPaymentMode = (id) => payModes.find(m => m.payMode === id)?.payMode || "";

    const sortPaymentsDesc = (paymentList) => {
        return [...paymentList].sort((a, b) => {
            const timeA = a.date?.toMillis ? a.date.toMillis() : new Date(a.date || 0).getTime();
            const timeB = b.date?.toMillis ? b.date.toMillis() : new Date(b.date || 0).getTime();
            return timeB - timeA;
        });
    };

    const reloadCustomerPayments = async (customerId) => {
        const paymentSnap = await getDocs(collection(db, "customer_history"));
        const allPayments = paymentSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setPayments(allPayments);
        setCustomerPayments(sortPaymentsDesc(allPayments.filter(p => p.customerId === customerId)));
    };

    const handleView = (customer) => {
        setSelectedCustomer(customer);
        setCustomerPayments(sortPaymentsDesc(payments.filter(p => p.customerId === customer.id)));
        setOpenHistory(true);
    };

    const handleSavePayment = async () => {
        if (!paymentForm.amount || !paymentForm.date || !paymentForm.paymentMode) {
            return Toast.fire({ icon: "error", title: "Fill required fields" });
        }
        const data = {
            ...paymentForm,
            amount: Number(paymentForm.amount),
            customerId: selectedCustomer.id,
            date: Timestamp.fromDate(new Date(paymentForm.date)),
            type: "Account",
        };
        try {
            if (editPaymentId) await updateDoc(doc(db, "customer_history", editPaymentId), data);
            else await addDoc(collection(db, "customer_history"), data);
            setOpenPayment(false);
            await reloadCustomerPayments(selectedCustomer.id);
            Toast.fire({ icon: "success", title: "Payment Saved" });
        } catch (error) {
            Swal.fire("Error", "Failed to save payment", "error");
        }
    };

    const handleDeletePayment = async (id) => {
        if (window.confirm("Are you sure you want to delete this payment?")) {
            await deleteDoc(doc(db, "customer_history", id));
            await reloadCustomerPayments(selectedCustomer.id);
            Toast.fire({ icon: "success", title: "Deleted" });
        }
    };

    const formatMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    const tightCell = { py: 0.5, px: 1.5 };

    return (
        <Box p={2} sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>

            {/* Header / Select Thought Dropdown */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight="bold" color="text.primary">
                    Thought-wise Customer Accounts Report
                </Typography>
                
                <TextField
                    select
                    size="small"
                    label="Select Thought (Reference)"
                    value={selectedThroughId}
                    onChange={(e) => {
                        setSelectedThroughId(e.target.value);
                        setPage(0);
                    }}
                    sx={{ width: "320px", bgcolor: "white", borderRadius: 1 }}
                >
                    {throughList.map(t => (
                        <MenuItem key={t.id} value={t.id}>
                            {t.name} {t.mobile ? `(${t.mobile})` : ""}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {/* Main Table displaying customers under selected thought */}
            <Paper elevation={1} sx={{ borderRadius: 1.5, overflow: "hidden" }}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#1976d2" }}>
                        <TableRow>
                            <TableCell sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>#</TableCell>
                            <TableCell sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Customer Name</TableCell>
                            <TableCell sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Mobile</TableCell>
                            <TableCell align="center" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Trips</TableCell>
                            <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Sale</TableCell>
                            <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Paid</TableCell>
                            <TableCell align="right" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Balance</TableCell>
                            <TableCell align="center" sx={{ ...tightCell, color: "white", fontWeight: "bold" }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredCustomers.length === 0 ? (
                            <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>No customers found for this thought reference</TableCell></TableRow>
                        ) : (
                            filteredCustomers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((c, i) => (
                                <TableRow key={c.id} hover sx={{ '&:hover': { bgcolor: "#f1f5f9" } }}>
                                    <TableCell sx={tightCell}>{page * rowsPerPage + i + 1}</TableCell>
                                    <TableCell sx={tightCell}>
                                        <Typography variant="body2" fontWeight="600">{c.customerName}</Typography>
                                    </TableCell>
                                    <TableCell sx={tightCell}>
                                        <Typography variant="caption" color="text.secondary">{c.mobile || "-"}</Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={tightCell}>
                                        <Chip label={c.totalTrips} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.75rem' }} />
                                    </TableCell>
                                    <TableCell align="right" sx={tightCell}>
                                        <Typography variant="body2">{formatMoney(c.totalSale)}</Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={tightCell}>
                                        <Typography variant="body2" color="success.main" fontWeight="500">
                                            {formatMoney(c.totalPaid)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right" sx={tightCell}>
                                        <Chip
                                            label={formatMoney(c.remaining)}
                                            color={c.remaining > 0 ? "error" : "success"}
                                            sx={{ height: 22, fontWeight: 'bold', fontSize: '0.75rem', borderRadius: 1 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center" sx={tightCell}>
                                        <Tooltip title="View Payment History">
                                            <IconButton size="small" color="primary" onClick={() => handleView(c)} sx={{ p: 0.5, bgcolor: '#e3f2fd' }}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>

                <TablePagination
                    rowsPerPageOptions={[10]}
                    component="div"
                    count={filteredCustomers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    sx={{ borderTop: "1px solid #e0e0e0", minHeight: '40px', '.MuiTablePagination-toolbar': { minHeight: '40px' } }}
                />
            </Paper>

            {/* PAYMENT HISTORY DIALOG */}
            <Dialog open={openHistory} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 1.5 } }}>
                <DialogTitle sx={{ bgcolor: "#1976d2", color: "white", display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, px: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="subtitle1" fontWeight="bold">{selectedCustomer?.customerName}</Typography>
                        <Chip label="Payment History" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', height: 20 }} />
                    </Box>
                    <Button variant="contained" color="success" size="small" disableElevation onClick={() => {
                        setEditPaymentId(null);
                        setPaymentForm({ amount: "", paymentMode: "", note: "", date: new Date().toISOString().split("T")[0] });
                        setOpenPayment(true);
                    }}>+ Add</Button>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: "#f1f5f9" }}>
                            <TableRow>
                                <TableCell sx={tightCell}>Date</TableCell>
                                <TableCell sx={tightCell}>Note</TableCell>
                                <TableCell sx={tightCell}>Mode</TableCell>
                                <TableCell align="right" sx={tightCell}>Amount</TableCell>
                                <TableCell align="center" sx={tightCell}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customerPayments.length === 0 ? (
                                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 2 }}>No history found</TableCell></TableRow>
                            ) : (
                                customerPayments.map(p => (
                                    <TableRow key={p.id} hover>
                                        <TableCell sx={tightCell}>{p.date?.toDate ? p.date.toDate().toLocaleDateString('en-GB') : "N/A"}</TableCell>
                                        <TableCell sx={tightCell}><Typography variant="body2">{p.note || "-"}</Typography></TableCell>
                                        <TableCell sx={tightCell}><Chip label={getPaymentMode(p.paymentMode)} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} /></TableCell>
                                        <TableCell align="right" sx={tightCell}><Typography variant="body2" fontWeight="bold" color="success.main">{formatMoney(p.amount)}</Typography></TableCell>
                                        <TableCell align="center" sx={tightCell}>
                                            <IconButton size="small" color="primary" sx={{ p: 0.5 }} onClick={() => {
                                                setEditPaymentId(p.id);
                                                setPaymentForm({ amount: p.amount, paymentMode: p.paymentMode, note: p.note || "", date: p.date?.toDate().toISOString().split("T")[0] });
                                                setOpenPayment(true);
                                            }}><Edit fontSize="small" /></IconButton>
                                            <IconButton size="small" color="error" sx={{ p: 0.5 }} onClick={() => handleDeletePayment(p.id)}><Delete fontSize="small" /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions sx={{ p: 1, borderTop: '1px solid #eee' }}>
                    <Button onClick={() => setOpenHistory(false)} size="small" color="inherit">Close</Button>
                </DialogActions>
            </Dialog>

            {/* ADD / EDIT PAYMENT DIALOG */}
            <Dialog open={openPayment} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 1.5 } }}>
                <DialogTitle sx={{ py: 1.5, px: 2, fontSize: '1rem', fontWeight: 'bold' }}>
                    {editPaymentId ? "Edit Payment" : "New Payment"}
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>
                    <TextField label="Date" type="date" size="small" InputLabelProps={{ shrink: true }} value={paymentForm.date} onChange={(e) => setPaymentForm({ ...paymentForm, date: e.target.value })} />
                    <TextField label="Amount (₹)" type="number" size="small" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} />
                    <TextField select label="Mode" size="small" value={paymentForm.paymentMode} onChange={(e) => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}>
                        {payModes.map(m => <MenuItem key={m.id} value={m.payMode} sx={{ fontSize: '0.85rem' }}>{m.payMode}</MenuItem>)}
                    </TextField>
                    <TextField label="Note" size="small" value={paymentForm.note} onChange={(e) => setPaymentForm({ ...paymentForm, note: e.target.value })} />
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 1.5 }}>
                    <Button onClick={() => setOpenPayment(false)} size="small" color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSavePayment} size="small" disableElevation>Save</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
}

export default Thoughcustreport;